import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import { plannedTimeline, MAX_HOURS, type PlannedRow, type CanAssign } from './plannedTimeline'
import { appLocale } from '@/lib/dateUtils'

/**
 * When the plan puts the load, hour by hour.
 *
 * Shown only once a request is selected, because across several requests the shape is a sum of
 * unrelated plans and means nothing — the peak would be an artefact of how many requests happen
 * to be open rather than of any one recovery.
 */
export function PlannedTimelineStrip({ rows, canAssign }: { rows: PlannedRow[]; canAssign?: CanAssign }) {
    const { t } = useTranslation()
    const plan = useMemo(() => plannedTimeline(rows, canAssign), [rows, canAssign])

    if (plan.hours.length === 0 && plan.unscheduled === 0) return null

    const fmtHour = (d: Date) => String(d.getHours()).padStart(2, '0')
    const dayOf = (d: Date) => d.toLocaleDateString(appLocale(), { day: 'numeric', month: 'short' })

    // A day label only where the day changes, so a multi-day plan reads without repeating itself.
    let lastDay = ''
    const spanLabel = plan.hours.length > 0
        ? `${dayOf(plan.hours[0].hour)} ${fmtHour(plan.hours[0].hour)}:00 – ${dayOf(plan.hours[plan.hours.length - 1].hour)} ${fmtHour(plan.hours[plan.hours.length - 1].hour)}:00`
        : ''

    // A line, not a card. One fact about the plan, read in passing on the way to the table —
    // a bordered panel gave it the weight of a section of its own.
    return (
        <div className="mb-3 flex items-center gap-4 border-b pb-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap flex-shrink-0">
                {t('assignment.plannedPerHour', 'Objects starting per hour')}
            </span>
            {spanLabel && <span className="text-[11px] text-muted-foreground whitespace-nowrap flex-shrink-0">{spanLabel}</span>}
            <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-shrink-0 order-last">
                    <span>
                        {t('assignment.plannedPeak', 'Peak {{n}} in one hour', { n: plan.peak })}
                    </span>
                    <span className="tabular-nums">
                        {t('assignment.plannedTotal', '{{n}} scheduled', { n: plan.total })}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <span className="inline-block h-2 w-2 rounded-sm" style={{ backgroundColor: 'rgb(151,200,100)' }} />
                        {t('recoverRequests.assign', 'Assign')}
                        <span className="inline-block h-2 w-2 rounded-sm ml-1" style={{ backgroundColor: 'rgb(232,96,55)' }} />
                        {t('assignment.wait', 'Wait')}
                    </span>
                    {plan.unscheduled > 0 && (
                        <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400"
                              title={t('assignment.plannedUnscheduledHint',
                                  'These objects have no forecast start time, so they are not on the strip. They still need assigning.')}>
                            <AlertTriangle className="h-3 w-3" />
                            {t('assignment.plannedUnscheduled', '{{n}} unscheduled', { n: plan.unscheduled })}
                        </span>
                    )}
            </div>

            {plan.hours.length > 0 && (
                <div className="min-w-0 flex-1">
                    {/* Bars STRETCH to the width available rather than sitting at a fixed 16px.
                        The shape of the load is the whole point, and squeezed into a corner it
                        was unreadable. */}
                    <div className="flex items-end gap-[2px] w-full h-[34px]">
                        {plan.hours.map((h) => {
                            // Scale to the peak, not to a fixed maximum — the question is where the
                            // load concentrates, and against a fixed scale a quiet plan reads flat.
                            const pct = plan.peak > 0 ? (h.count / plan.peak) * 100 : 0
                            const day = dayOf(h.hour)
                            const showDay = day !== lastDay
                            if (showDay) lastDay = day
                            return (
                                <div key={h.hour.getTime()}
                                     className="group relative flex flex-col items-center gap-1 flex-1 min-w-[8px] max-w-[44px]">
                                    {/* The count on hover. A permanent label per bar does not fit
                                        once the strip spans a day, and the shape is what is read
                                        at a glance — the number is what is read on purpose. */}
                                    {h.count > 0 && (
                                        <div className="pointer-events-none absolute -top-1 left-1/2 -translate-x-1/2 -translate-y-full z-20 hidden group-hover:block whitespace-nowrap rounded border bg-popover px-2 py-1 text-[11px] shadow-md">
                                            <div className="font-semibold tabular-nums">
                                                {t('assignment.plannedHourCount', '{{n}} objects', { n: h.count })}
                                            </div>
                                            <div className="text-muted-foreground">
                                                {h.hour.toLocaleString(appLocale(), { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <span style={{ color: 'rgb(101,150,58)' }} className="tabular-nums font-medium">
                                                    {h.go} {t('recoverRequests.assign', 'Assign')}
                                                </span>
                                                <span style={{ color: 'rgb(180,74,42)' }} className="tabular-nums font-medium">
                                                    {h.wait} {t('assignment.wait', 'Wait')}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    {/* STACKED: what can be handed out now sits under what cannot,
                                        so the red is the part of each hour that needs unblocking
                                        rather than an engineer. */}
                                    <div className="w-full h-[22px] flex flex-col justify-end">
                                        {h.count > 0 ? (
                                            <div className="w-full flex flex-col justify-end rounded-t-[2px] overflow-hidden"
                                                 style={{ height: `${Math.max(pct, 10)}%` }}>
                                                <div style={{
                                                    height: `${(h.wait / h.count) * 100}%`,
                                                    backgroundColor: 'rgb(232,96,55)',
                                                }} />
                                                <div style={{
                                                    height: `${(h.go / h.count) * 100}%`,
                                                    backgroundColor: 'rgb(151,200,100)',
                                                }} />
                                            </div>
                                        ) : (
                                            // An empty hour keeps a hairline so the axis reads as
                                            // continuous time rather than as missing bars.
                                            <div className="w-full" style={{ height: '1px', backgroundColor: 'hsl(var(--border))' }} />
                                        )}
                                    </div>
                                    <span className={`text-[9px] tabular-nums leading-none ${showDay ? 'font-semibold text-foreground' : 'text-muted-foreground/70'}`}>
                                        {fmtHour(h.hour)}
                                    </span>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}

            {plan.hours.length >= MAX_HOURS && (
                <div className="text-[11px] text-muted-foreground flex-shrink-0">
                    {t('assignment.plannedTruncated',
                        'Showing the first {{n}} hours — the plan runs longer than the strip.', { n: MAX_HOURS })}
                </div>
            )}
        </div>
    )
}
